import React, { useEffect, useState } from 'react';
import { AiChatAssistant } from '../../../components/AiChatAssistant/AiChatAssistant';
import { DocumentList, Document } from '../../../components/DocumentList/DocumentList';
import { DocumentViewer } from '../../../components/DocumentViewer/DocumentViewer';
import { useDocumentsApi } from '../../../hooks/useDocumentsApi';
import { Header } from '../../../components/Header/Header';
import { useAuthStore } from '../../../stores/auth.store';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// Lightweight toast
const Toast: React.FC<{ message: string; onClose: () => void }> = ({ message, onClose }) => (
  <div
    className="fixed bottom-4 right-4 bg-red-600 text-white px-4 py-2 rounded shadow-md z-50"
    onClick={onClose}
  >
    {message}
  </div>
);

export const DashboardPage: React.FC = () => {
  const storeLogout = useAuthStore((s) => s.logout);
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const api = useDocumentsApi();
  const { t } = useTranslation();

  const logout = async () => {
    await storeLogout();
    navigate('/login');
  };
  const [docs, setDocs] = useState<Document[]>([]);
  const [selected, setSelected] = useState<Document | undefined>();
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [processingIds, setProcessingIds] = useState<string[]>([]);

  const refreshList = () => {
    api
      .list()
      .then((response) => {
        const mappedDocs: Document[] = response.data
          .map((d) => {
            const lifecycle = (d.status || '').toUpperCase();
            let normalizedStatus: Document['status'];
            if (lifecycle === 'ARCHIVED' || lifecycle === 'DELETED') {
              normalizedStatus = 'archived';
            } else if (lifecycle === 'ACTIVE') {
              const step = (d.step || '').toUpperCase();
              normalizedStatus =
                step === 'READY'
                  ? 'ready'
                  : step === 'ERROR'
                    ? 'error'
                    : step === 'ARCHIVE'
                      ? 'archived'
                      : 'processing';
            } else {
              const step = (d.step || '').toUpperCase();
              normalizedStatus =
                step === 'READY'
                  ? 'ready'
                  : step === 'ERROR'
                    ? 'error'
                    : step === 'ARCHIVE'
                      ? 'archived'
                      : 'processing';
            }

            const item: Document = {
              id: d._id,
              name: d.originalFileName,
              status: normalizedStatus,
              tags: d.tags || [],
              mimeType: d.mimeType,
              date: new Date(d.createdAt),
              validated: Boolean(d.isValid),
              documentType: d.documentType,
              metadata: d.metadata,
            };
            return item;
          })
          .sort((a, b) => b.date.getTime() - a.date.getTime());
        setDocs(mappedDocs);
        // Auto-watch any documents stuck in processing without an active watcher
        mappedDocs
          .filter((d) => d.status === 'processing')
          .forEach((d) => {
            if (!processingIds.includes(d.id)) {
              pollDocumentStatus(d.id);
            }
          });
      })
      .catch(() => setToastMsg(t('toast.documents.loadError')));
  };

  useEffect(() => {
    refreshList();
  }, []);

  const handleDocumentArchived = (docId: string) => {
    setDocs((prevDocs: Document[]) => {
      const updated: Document[] = prevDocs.map(
        (doc): Document =>
          doc.id === docId ? { ...doc, status: 'archived' as Document['status'] } : doc,
      );
      updated.sort((a, b) => b.date.getTime() - a.date.getTime());
      if (selected?.id === docId) {
        setSelected(undefined);
      }
      return updated;
    });
  };

  const pollDocumentStatus = (id: string) => {
    let attempts = 0;
    const maxAttempts = 60; // ~2 minutes at 2s interval
    setProcessingIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    const interval = setInterval(async () => {
      attempts++;
      try {
        const d = await api.get(id);
        const mapped = {
          id: d._id,
          name: d.originalFileName,
          status:
            (d.status || '').toUpperCase() === 'ARCHIVED' ||
            (d.status || '').toUpperCase() === 'DELETED'
              ? 'archived'
              : (d.step || '').toUpperCase() === 'READY'
                ? 'ready'
                : (d.step || '').toUpperCase() === 'ERROR'
                  ? 'error'
                  : (d.step || '').toUpperCase() === 'ARCHIVE'
                    ? 'archived'
                    : 'processing',
          tags: d.tags || [],
          mimeType: d.mimeType,
          date: new Date(d.createdAt),
          validated: Boolean(d.isValid),
          documentType: d.documentType,
          metadata: d.metadata,
        } as Document;
        setDocs((prev) => {
          const updated = prev.map((doc) => (doc.id === id ? mapped : doc));
          updated.sort((a, b) => b.date.getTime() - a.date.getTime());
          return updated;
        });
        if (
          mapped.status === 'ready' ||
          mapped.status === 'error' ||
          mapped.status === 'archived'
        ) {
          clearInterval(interval);
          if (mapped.status === 'ready') toast.success(t('toast.processing.success'));
          else if (mapped.status === 'error') toast.error(t('toast.processing.error'));
          setProcessingIds((prev) => prev.filter((x) => x !== id));
        }
      } catch (e) {
        clearInterval(interval);
        toast.error(t('toast.processing.updateError'));
        setProcessingIds((prev) => prev.filter((x) => x !== id));
      }
      if (attempts >= maxAttempts) {
        clearInterval(interval);
        toast(t('toast.processing.delay'), { icon: '⏳' });
        setProcessingIds((prev) => prev.filter((x) => x !== id));
      }
    }, 2000);
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Header userName={user?.email || 'User'} onLogout={logout} />
      <main className="flex-1 overflow-hidden">
        <div className="container mx-auto px-4 h-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 py-4 md:py-6 h-full">
            {/* Left Column: Document List */}
            <div className="lg:col-span-3 min-h-0 overflow-y-auto">
              <DocumentList
                documents={docs}
                selectedDocument={selected}
                onSelectDocument={setSelected}
                onDocumentArchived={handleDocumentArchived}
                processingIds={processingIds}
                onProcessingStarted={() => {
                  toast(t('toast.processing.start'), { icon: '⚙️' });
                }}
                onUploadCompleted={(docId) => {
                  toast(t('toast.processing.progress'), { icon: '🔍' });
                  pollDocumentStatus(docId);
                  refreshList();
                }}
                onError={() => toast.error(t('toast.processing.error'))}
              />
            </div>

            {/* Middle Column: Document Viewer */}
            <div className="lg:col-span-5 min-h-0 overflow-y-auto">
              <DocumentViewer
                document={selected}
                onDocumentChange={(updated) => {
                  setSelected(updated);
                  setDocs((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
                }}
              />
            </div>

            {/* Right Column: AI Chat Assistant */}
            <div className="lg:col-span-4 min-h-0">
              <AiChatAssistant documentId={selected?.id} />
            </div>
          </div>
        </div>
      </main>
      {toastMsg && <Toast message={toastMsg} onClose={() => setToastMsg(null)} />}
    </div>
  );
};
